import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDeals } from '../utils/api';

function formatMoney(v) {
  if (!v && v !== 0) return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

function formatMoneyFull(v) {
  if (!v && v !== 0) return '—';
  return `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const STATUS_META = {
  active:         { label: 'Active',          cls: 'badge-green' },
  under_contract: { label: 'Under Contract',  cls: 'badge-amber' },
  closed:         { label: 'Closed',          cls: 'badge-neutral' },
  dead:           { label: 'Dead',            cls: 'badge-red' },
};

const ROLE_LABELS = { equity: 'Equity', debt: 'Debt', broker: 'Broker', service: 'Service' };

const CAT_CLASSES = {
  lp_cogp: 'avatar-lp_cogp', lender: 'avatar-lender',
  broker: 'avatar-broker', partner_service: 'avatar-partner_service', seller_operator: 'avatar-seller_operator',
};

function ContactBadge({ contact }) {
  return (
    <Link to={`/contacts/${contact.id}`} title={`${contact.first_name} ${contact.last_name} — ${ROLE_LABELS[contact.role] || contact.role}`}
      style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <div className={`contact-avatar contact-avatar-xs ${CAT_CLASSES[contact.category]}`}>
        {contact.first_name?.[0]}{contact.last_name?.[0]}
      </div>
      <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
        {contact.first_name} {contact.last_name}
      </span>
      <span className="badge badge-neutral" style={{ fontSize: '0.65rem' }}>
        {ROLE_LABELS[contact.role] || contact.role}
      </span>
    </Link>
  );
}

export default function DealsListPage() {
  const [data, setData] = useState({ deals: [], summary: [], leaderboard: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    const params = {};
    if (filterStatus) params.status = filterStatus;
    if (search) params.search = search;
    getDeals(params)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [search, filterStatus]);

  const getSummary = (status) => {
    const s = data.summary.find(r => r.status === status);
    return s ? { count: parseInt(s.count), value: parseFloat(s.total_value) || 0 } : { count: 0, value: 0 };
  };

  const grouped = {};
  for (const deal of data.deals) {
    if (!grouped[deal.status]) grouped[deal.status] = [];
    grouped[deal.status].push(deal);
  }

  const statuses = ['active', 'under_contract', 'closed', 'dead'];

  if (loading) return <div className="page-loading">Loading deals…</div>;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Deal Pipeline</h1>
          <p className="page-subtitle">
            {getSummary('active').count} active · {getSummary('under_contract').count} under contract
          </p>
        </div>
        <Link to="/deals/new" className="btn btn-primary">+ New Deal</Link>
      </div>

      {/* Status summary cards */}
      <div className="metric-row">
        {statuses.map(s => {
          const { count, value } = getSummary(s);
          const meta = STATUS_META[s];
          return (
            <button
              key={s}
              className={`metric-card clickable ${filterStatus === s ? 'active' : ''}`}
              onClick={() => setFilterStatus(filterStatus === s ? '' : s)}
            >
              <div className="metric-label">{meta.label}</div>
              <div className="metric-value">{count}</div>
              {value > 0 && <div className="metric-sub">{formatMoney(value)}</div>}
            </button>
          );
        })}
      </div>

      {/* Closed leaderboard */}
      {data.leaderboard.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">Closed Deals Leaderboard</div>
          {data.leaderboard.map((m, i) => (
            <div key={m.id} className={`leaderboard-row ${i === 0 ? 'leaderboard-first' : ''}`}>
              <span className="leaderboard-rank">#{i + 1}</span>
              <span className="leaderboard-name">{m.display_name}</span>
              <span className="leaderboard-deals">{m.deal_count} deal{m.deal_count !== '1' ? 's' : ''}</span>
              <span className="leaderboard-value">{formatMoneyFull(m.total_value)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input
          className="input"
          placeholder="Search deals by name or address…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      </div>

      {/* Deal rows by status */}
      {statuses.map(s => {
        const deals = grouped[s] || [];
        if (filterStatus && filterStatus !== s) return null;
        if (deals.length === 0) return null;
        const meta = STATUS_META[s];

        return (
          <div key={s} className="section">
            <h3 className="section-label">
              {meta.label}
              <span style={{ marginLeft: 8, color: 'var(--color-text-muted)', fontWeight: 400, fontSize: '0.8rem' }}>
                ({deals.length})
              </span>
            </h3>
            <div className="card">
              {deals.map((deal, i) => {
                const displayValue = deal.purchase_price || deal.underwriting_price || deal.whisper_price;
                return (
                  <React.Fragment key={deal.id}>
                    <Link to={`/deals/${deal.id}`} className="deal-row">
                      <div className="deal-row-main">
                        <div className="deal-row-name">{deal.name}</div>
                        <div className="deal-row-address">{deal.address}</div>
                        {(deal.contacts || []).length > 0 && (
                          <div className="deal-row-contacts">
                            {deal.contacts.slice(0, 4).map(c => (
                              <ContactBadge key={c.id} contact={c} />
                            ))}
                            {deal.contacts.length > 4 && (
                              <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                                +{deal.contacts.length - 4} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="deal-row-right">
                        {displayValue && (
                          <div className="deal-row-value">{formatMoneyFull(displayValue)}</div>
                        )}
                        {deal.driver_name && (
                          <div className="deal-row-driver">{deal.driver_name}</div>
                        )}
                        {deal.close_date && (
                          <div className="deal-row-date">
                            {new Date(deal.close_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                      </div>
                    </Link>
                    {i < deals.length - 1 && <div className="row-divider" />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      })}

      {data.deals.length === 0 && (
        <div className="empty-state">
          <p>No deals found.</p>
          <Link to="/deals/new" className="btn btn-primary">Add your first deal</Link>
        </div>
      )}
    </div>
  );
}
